import { compose, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Form as SemanticForm } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import queryString from 'query-string';

import {
  ErrorMessage,
  Field,
  Form,
  withFormik,
} from 'formik';

import { globalTheme as globalColor, InputFormField } from '@playbookux/playbook-web-core';
import { createUser } from '../../../core/services/user';
import { setLinkedInState, signInWithPassword } from '../../../core/actions';
import PasswordPopup from '../../../core/components/password-popup';
import schema from './schema';
import { getCompanyInvitation } from './request';
import { config } from '../../../config';

import {
  BottomContainer,
  Button,
  Checkbox,
  Container,
  ContainerFirstName,
  ContainerLastName,
  ContainerName,
  Divider,
  Error,
  ErrorName,
  ErrorSubmit,
  HaveAccount,
  LinkedInIcon,
  Logo,
  PrivacyPolicy,
  PrivacyPolicyContainer,
  Segment,
  Title,
} from './styles';
import withGa from '../../../core/components/with-ga';

class SignUp extends Component {
  constructor(props) {
    super(props);

    this.state = {
      showConfirmPassword: false,
      showPassword: false,
      uri: null,
      emailDisabled: false,
      passwordPopupOpen: false,
      invalidInvite: false,
      pendingInvite: false,
    };
  }

  componentDidMount() {
    const state = Math.random()
      .toString(36)
      .slice(2);

    const { invitation, inviteLink } = queryString.parse(this.props.location.search);

    if (inviteLink) {
      this.props.setFieldValue('inviteLink', inviteLink);
    }

    if (invitation) {
      this.setState({ pendingInvite: true });
      getCompanyInvitation(invitation)
        .then(({ email, message }) => {
          if (message?.includes("Couldn't find CompanyInvite")) {
            this.setState({ invalidInvite: true });
            this.setState({ pendingInvite: false });
            return;
          }
          this.props.setFieldValue('email', email);
          this.setState({ emailDisabled: true });
          this.setState({ pendingInvite: false });
        })
        .catch(() => { /* do nothing */ });
    }

    const uri = `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${
      config.REACT_APP_LINKEDIN_CLIENT_ID
    }&redirect_uri=${config.REACT_APP_LINKEDIN_REDIRECT_URI}&state=${state}&scope=${
      config.REACT_APP_LINKEDIN_SCOPE
    }`;

    this.setState({ uri });
    this.props.setLinkedInState(state);
  }

  onShowPasswordClicked = () => {
    this.setState((state) => ({ showPassword: !state.showPassword }));
  };

  onShowConfirmPasswordClicked = () => {
    this.setState((state) => ({ showConfirmPassword: !state.showConfirmPassword }));
  };

  render() {
    const { handleSubmit, status, values } = this.props;
    const {
      showPassword,
      showConfirmPassword,
      uri,
      emailDisabled,
      passwordPopupOpen,
      invalidInvite,
      pendingInvite,
    } = this.state;
    const { textSignScreen: labelColor } = globalColor.color;
    const error = status && status.error;
    const isLoading = status && status.isLoading;

    if (invalidInvite) {
      return (
        <Container>
          <Logo src="/images/playbook-logo.png" alt="Playbook" />
          <Segment>
            This invitation is no longer active.
            <br />
            {' '}
            <br />
            Please contact your PlaybookUX administrator to learn more.
          </Segment>
        </Container>
      );
    }
    if (!pendingInvite) {
      return (
        <Container>
          <Logo src="/images/playbook-logo.png" alt="Playbook" />

          <Segment>
            <Title>
              {values.inviteLink ? (
                'Join your team on PlaybookUX'
              ) : (
                'Company Sign Up'
              )}
            </Title>

            <Form>
              <SemanticForm as="div">
                <SemanticForm.Field>
                  <ContainerName>
                    <ContainerFirstName>
                      <Field
                        component={InputFormField}
                        labelColor={labelColor}
                        labelFontSize="14px"
                        labelText="First name"
                        name="firstName"
                      />
                      <ErrorMessage component={ErrorName} name="firstName" />
                    </ContainerFirstName>

                    <ContainerLastName>
                      <Field
                        component={InputFormField}
                        labelColor={labelColor}
                        labelFontSize="14px"
                        labelText="Last name"
                        name="lastName"
                      />
                      <ErrorMessage component={ErrorName} name="lastName" />
                    </ContainerLastName>
                  </ContainerName>
                </SemanticForm.Field>

                <Field
                  component={InputFormField}
                  labelColor={labelColor}
                  labelFontSize="14px"
                  labelText="Email address"
                  disabled={emailDisabled}
                  name="email"
                />
                <ErrorMessage component={Error} name="email" />

                <Field
                  labelColor={labelColor}
                  labelFontSize="14px"
                  name="password"
                >
                  {({
                    field,
                    form,
                    meta,
                  }) => (
                    <InputFormField
                      labelText="Password"
                      type={!showPassword ? 'password' : 'text'}
                      icon={{
                        link: true,
                        name: !showPassword ? 'eye' : 'eye slash',
                        onClick: this.onShowPasswordClicked,
                      }}
                      form={form}
                      meta={meta}
                      field={field}
                      onFocus={() => this.setState({ passwordPopupOpen: true })}
                      onBlur={() => this.setState({ passwordPopupOpen: false })}
                    />
                  )}
                </Field>

                <PasswordPopup
                  isOpen={passwordPopupOpen}
                  password={values.password}
                />

                <ErrorMessage component={Error} name="password" />

                <Field
                  component={InputFormField}
                  labelColor={labelColor}
                  labelFontSize="14px"
                  labelText="Confirm Password"
                  name="confirmPassword"
                  type={!showConfirmPassword ? 'password' : 'text'}
                  icon={{
                    link: true,
                    name: !showConfirmPassword ? 'eye' : 'eye slash',
                    onClick: this.onShowConfirmPasswordClicked,
                  }}
                />
                <ErrorMessage component={Error} name="confirmPassword" />

                <Checkbox
                  label="Keep me signed in"
                  name="keepSignedIn"
                />

                <BottomContainer>
                  <Button
                    loading={isLoading}
                    onClick={handleSubmit}
                    type="submit"
                  >
                    Create an Account
                  </Button>

                  {error && <ErrorSubmit>{error}</ErrorSubmit>}

                  <Divider />

                  <Button href={encodeURI(uri)} disabled={isLoading}>
                    <LinkedInIcon />
                    Sign Up with Linkedin
                  </Button>
                </BottomContainer>
              </SemanticForm>
            </Form>
          </Segment>

          <HaveAccount>
            Have an account?
            {' '}
            <Link to="/sign-in">Sign in</Link>
          </HaveAccount>

          <PrivacyPolicyContainer>
            <PrivacyPolicy>
              By using Playbook you agree to the
              {' '}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.playbookux.com/terms-of-service-clients/"
              >
                Terms & Conditions
              </a>
              {' '}
              and
              {' '}
              <a target="_blank" rel="noopener noreferrer" href="https://www.playbookux.com/privacy-policy-company/">Privacy Policy</a>
            </PrivacyPolicy>
          </PrivacyPolicyContainer>
        </Container>
      );
    }
    return (
      <Container>
        <Logo src="/images/playbook-logo.png" alt="Playbook" />
        <Segment>
          Loading invite...
        </Segment>

        <HaveAccount>
          Have an account?
          {' '}
          <Link to="/sign-in">Sign in</Link>
        </HaveAccount>

        <PrivacyPolicyContainer>
          <PrivacyPolicy>
            By using Playbook UX LLC you agree to the
            {' '}
            <a target="_blank" rel="noopener noreferrer" href="https://www.playbookux.com/terms-of-service-clients/">Terms & Conditions</a>
            {' '}
            and
            {' '}
            <a target="_blank" rel="noopener noreferrer" href="https://www.playbookux.com/privacy-policy-company/">Privacy Policy</a>
          </PrivacyPolicy>
        </PrivacyPolicyContainer>
      </Container>
    );
  }
}

SignUp.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  setLinkedInState: PropTypes.func.isRequired,
  status: PropTypes.object,
  location: PropTypes.object.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  values: PropTypes.object.isRequired,
};

SignUp.defaultProps = {
  status: {},
};

const mapDispatchToProps = (dispatch) => bindActionCreators({
  setLinkedInState,
  signInWithPassword,
}, dispatch);

export default compose(
  connect(null, mapDispatchToProps),
  withFormik({
    validationSchema: schema,
    mapPropsToValues: () => ({
      confirmPassword: '',
      email: '',
      firstName: '',
      lastName: '',
      password: '',
      inviteLink: null,
    }),
    handleSubmit: async (values, { props, setStatus }) => {
      setStatus({
        error: null,
        isLoading: true,
      });

      try {
        await createUser(values);

        const { email, password, inviteLink } = values;

        await props.signInWithPassword(email, password);
        if (inviteLink) {
          props.history.push(`/invite/${inviteLink}?awaitApproval=${true}`);
        }
      } catch (err) {
        const error = (err && err.code && err.message)
          ? err.message
          : 'Error while creating the account';

        setStatus({
          error,
          isLoading: false,
        });
      }
    },
  }),
  withGa,
)(SignUp);
